import pako from 'pako';
import { ByteCursor, DicomParseError } from './byteCursor';
import { Charset, DEFAULT_CHARSET } from './charset';
import { DicomDataset, DicomElement, DicomItem } from './dataset';
import { vrForTag } from './dictionary';
import { WORKSPACE_IMPLEMENTATION, buildFileMeta } from './fileMeta';
import {
    FILE_META_GROUP_LENGTH_TAG,
    ITEM_DELIMITATION_TAG,
    ITEM_TAG,
    PIXEL_DATA_TAG,
    SEQUENCE_DELIMITATION_TAG,
    Tag,
    TRANSFER_SYNTAX_UID_TAG,
    UNDEFINED_LENGTH,
    formatTag,
    makeTag,
    tagGroup,
} from './tag';
import {
    EXPLICIT_VR_LITTLE_ENDIAN,
    FILE_META_TRANSFER_SYNTAX,
    IMPLICIT_VR_LITTLE_ENDIAN,
    TransferSyntax,
    transferSyntaxFor,
} from './transferSyntax';
import { detectTransferSyntax } from './transferSyntaxDetection';
import { Vr, isVr, resolveVr, usesLongFormLength } from './vr';

/** The fixed size of the PS3.10 file preamble. */
const PREAMBLE_LENGTH = 128;
/** The magic that follows the preamble in a PS3.10 file. */
const DICM_MAGIC = 'DICM';

/**
 * A parsed DICOM file: its preamble, its file meta group, and the dataset the
 * meta group describes.
 *
 * The preamble is retained rather than assumed to be zeroes because PS3.10
 * permits an application profile to put content there, and discarding it
 * would silently drop data on a read/write cycle. It is empty for a dataset
 * that arrived without one, which the writer renders as the zero-filled
 * preamble PS3.10 allows.
 */
export interface DicomFile {
    readonly preamble: Uint8Array;
    readonly meta: DicomDataset;
    readonly dataset: DicomDataset;
    readonly transferSyntax: TransferSyntax;
}

export interface ReadOptions {
    /**
     * Stop before this tag and leave the rest of the dataset unparsed.
     *
     * Used to index a file's headers without paying for its pixel data —
     * pass PIXEL_DATA_TAG to scan a directory of slices.
     */
    readonly stopBefore?: Tag;
}

/**
 * Parses a DICOM buffer, whether or not it is wrapped as a Part 10 file.
 *
 * A stored dataset with no preamble and no file meta group is read as itself:
 * its encoding is recovered from the element stream and it is described by a
 * meta group built from the dataset. Refusing those would refuse data sites
 * hold, and repairing them by rewriting the bytes before parsing — which is
 * how this was once done — leaves the parser reading a file nobody produced.
 *
 * Only the tag/length structure is walked eagerly; every value stays a view
 * over `buffer` until something asks for it. Nothing is copied, so the cost
 * of a parse is proportional to the file's element count rather than to its
 * size, and a 500 MB image series costs no more memory than the bytes
 * already read.
 */
export function readDicomFile(
    buffer: ArrayBuffer,
    options: ReadOptions = {}
): DicomFile {
    const bytes = new Uint8Array(buffer);
    const cursor = new ByteCursor(bytes);

    const preamble = readPreambleIfPresent(bytes, cursor);
    const meta = readFileMetaGroup(cursor);

    const declaredSyntaxUid = meta.string(TRANSFER_SYNTAX_UID_TAG);
    const transferSyntax =
        declaredSyntaxUid !== undefined
            ? transferSyntaxFor(declaredSyntaxUid)
            : detectDatasetSyntax(bytes, cursor.offset);
    const transferSyntaxUid = transferSyntax.uid;
    if (!transferSyntax.littleEndian) {
        // Element framing is byte-order aware, but a value's own bytes are
        // decoded little-endian throughout, so a big-endian dataset would
        // yield reversed numbers for Rows, PixelSpacing and pixel data —
        // wrong geometry that still looks like geometry. The syntax is
        // retired in PS3.5 and no fixture exercises it, so it is refused
        // rather than half-supported: a clear error beats a plausible
        // wrong answer.
        throw new DicomParseError(
            `Transfer syntax ${transferSyntaxUid} (Explicit VR Big Endian, ` +
                'retired) is not supported.',
            cursor.offset
        );
    }

    // Only the dataset is deflated; the preamble and the file meta group that
    // names the syntax are not, which is what makes such a file readable at
    // all. Once inflated the dataset is an ordinary explicit VR little endian
    // stream, and is read and described as one — this engine cannot deflate,
    // so an object that went on claiming the deflated syntax would be written
    // back as undeflated bytes under a declaration contradicting them.
    const datasetSyntax = transferSyntax.deflated
        ? transferSyntaxFor(EXPLICIT_VR_LITTLE_ENDIAN)
        : transferSyntax;
    const datasetCursor = transferSyntax.deflated
        ? new ByteCursor(inflateDataset(bytes, cursor.offset))
        : cursor;
    if (transferSyntax.deflated && meta.size > 0) {
        meta.setString(
            TRANSFER_SYNTAX_UID_TAG,
            EXPLICIT_VR_LITTLE_ENDIAN,
            'UI'
        );
    }

    const dataset = new DicomDataset();
    readElementsInto(
        dataset,
        datasetCursor,
        datasetCursor.length,
        datasetSyntax,
        options.stopBefore
    );

    return {
        preamble,
        // A dataset stored without a meta group is described by one built
        // from the dataset itself, so that what the file goes on to say about
        // the object always comes from the object. The group is left alone
        // wherever the file carried one, even an incomplete one: those are
        // bytes the file owns, and rewriting them would put this engine's
        // idea of the object into a file the caller only meant to read.
        meta:
            meta.size > 0
                ? meta
                : buildFileMeta(
                      dataset,
                      datasetSyntax.uid,
                      WORKSPACE_IMPLEMENTATION
                  ),
        dataset,
        transferSyntax: datasetSyntax,
    };
}

/**
 * Inflates the deflated dataset that follows a file meta group.
 *
 * PS3.5 Annex A.5 carries the dataset as a raw RFC 1951 stream — no zlib
 * wrapper and no checksum — so a reader that expects one finds a corrupt
 * header instead of a dataset.
 */
function inflateDataset(bytes: Uint8Array, offset: number): Uint8Array {
    try {
        return pako.inflateRaw(bytes.subarray(offset));
    } catch (error) {
        throw new DicomParseError(
            'The dataset declares the deflated transfer syntax but its bytes ' +
                `could not be inflated: ${
                    error instanceof Error ? error.message : String(error)
                }`,
            offset
        );
    }
}

/**
 * The syntax a dataset with no declared one is read under.
 *
 * A buffer that is not a DICOM element stream at all reaches this point too —
 * nothing before it has proved otherwise once the Part 10 header is optional —
 * so failing detection is also how a file that is not DICOM is refused.
 */
function detectDatasetSyntax(
    bytes: Uint8Array,
    offset: number
): TransferSyntax {
    const detected = detectTransferSyntax(bytes, offset);
    if (detected === undefined) {
        throw new DicomParseError(
            'The buffer declares no transfer syntax and its first elements do ' +
                'not read as one: it carries no file meta group, and no ' +
                'supported syntax walks its element stream. It is not a DICOM ' +
                'dataset.',
            offset
        );
    }
    return detected;
}

/**
 * Consumes the PS3.10 preamble and magic where the buffer carries them.
 *
 * A stored dataset need not: PS3.10 describes how a dataset is wrapped in a
 * file, and data that never went through that wrapping — a dataset written
 * straight off a network association, or exported by a tool that emits the
 * dataset alone — begins at its first element. Returns an empty preamble for
 * that case, which the writer renders as the zero-filled one PS3.10 allows.
 */
function readPreambleIfPresent(
    bytes: Uint8Array,
    cursor: ByteCursor
): Uint8Array {
    if (!startsWithMagicAt(bytes, cursor.offset)) {
        return new Uint8Array(0);
    }
    const preamble = cursor.readBytes(PREAMBLE_LENGTH);
    cursor.skip(DICM_MAGIC.length);
    return preamble;
}

/** True when 'DICM' sits one preamble past `offset`. */
function startsWithMagicAt(bytes: Uint8Array, offset: number): boolean {
    const at = offset + PREAMBLE_LENGTH;
    if (at + DICM_MAGIC.length > bytes.byteLength) {
        return false;
    }
    return (
        bytes[at] === 0x44 &&
        bytes[at + 1] === 0x49 &&
        bytes[at + 2] === 0x43 &&
        bytes[at + 3] === 0x4d
    );
}

/**
 * Reads the group-0002 file meta, which PS3.10 fixes as explicit VR little
 * endian regardless of the transfer syntax it goes on to name.
 *
 * Three shapes reach this point, and the group is identified by looking at it
 * rather than by reading and backing up:
 *
 * - the ordinary one, whose extent comes from (0002,0000);
 * - a group written without that length, which files assembled by hand and by
 *   some anonymisers are, read to the first element outside group 0002;
 * - no group at all, for a stored dataset that was never wrapped in a file,
 *   which yields an empty result and leaves the cursor on the first element of
 *   the dataset.
 */
function readFileMetaGroup(cursor: ByteCursor): DicomDataset {
    const meta = new DicomDataset();
    const firstTag = peekTag(cursor, FILE_META_TRANSFER_SYNTAX);
    if (firstTag === undefined || tagGroup(firstTag) !== 0x0002) {
        return meta;
    }
    if (firstTag !== FILE_META_GROUP_LENGTH_TAG) {
        readMetaUntilGroupChange(meta, cursor);
        return meta;
    }

    const first = readElementHeader(cursor, FILE_META_TRANSFER_SYNTAX);
    const groupLengthBytes = cursor.readBytes(first.length);
    meta.put(
        DicomElement.fromSource(
            first.tag,
            first.vr,
            groupLengthBytes,
            DEFAULT_CHARSET
        )
    );

    const declaredLength = new DataView(
        groupLengthBytes.buffer,
        groupLengthBytes.byteOffset,
        groupLengthBytes.byteLength
    ).getUint32(0, true);
    const end = cursor.offset + declaredLength;
    if (end > cursor.length) {
        throw new DicomParseError(
            `File meta group length ${declaredLength} runs past the end of the ` +
                `${cursor.length}-byte buffer.`,
            cursor.offset
        );
    }
    readElementsInto(meta, cursor, end, FILE_META_TRANSFER_SYNTAX, undefined);
    return meta;
}

function readMetaUntilGroupChange(
    meta: DicomDataset,
    cursor: ByteCursor
): void {
    while (!cursor.atEnd) {
        const tag = peekTag(cursor, FILE_META_TRANSFER_SYNTAX);
        if (tag === undefined || tagGroup(tag) !== 0x0002) {
            return;
        }
        readOneElementInto(meta, cursor, FILE_META_TRANSFER_SYNTAX);
    }
}

interface ElementHeader {
    readonly tag: Tag;
    /** The representation the value is decoded under. */
    readonly vr: Vr;
    /** What the stream carried, which differs for an over-long value. */
    readonly onWireVr: Vr;
    readonly length: number;
}

function readTag(cursor: ByteCursor, syntax: TransferSyntax): Tag {
    const group = cursor.readUint16(syntax.littleEndian);
    const element = cursor.readUint16(syntax.littleEndian);
    return makeTag(group, element);
}

/**
 * Reads one element's tag, VR and value length, leaving the cursor on the
 * value.
 *
 * In an implicit-VR stream the VR is not on the wire and comes from the
 * dictionary; an attribute the dictionary does not know resolves to UN, whose
 * bytes are preserved but not interpreted.
 */
function readElementHeader(
    cursor: ByteCursor,
    syntax: TransferSyntax
): ElementHeader {
    const tag = readTag(cursor, syntax);

    if (!syntax.explicitVr) {
        const vr = vrForTag(tag);
        const length = cursor.readUint32(syntax.littleEndian);
        return { tag, vr, onWireVr: vr, length };
    }

    const rawVr = cursor.readAscii(2);
    if (!isVr(rawVr)) {
        throw new DicomParseError(
            `Element ${formatTag(tag)} declares value representation '${rawVr}', ` +
                'which is not one of the representations in PS3.5; the stream is ' +
                'either malformed or not the explicit-VR syntax its file meta names.',
            cursor.offset - 2
        );
    }
    const onWireVr = resolveVr(rawVr);
    if (usesLongFormLength(onWireVr)) {
        cursor.skip(2); // Reserved.
        const length = cursor.readUint32(syntax.littleEndian);
        return {
            tag,
            vr: decodingVrFor(tag, onWireVr, length),
            onWireVr,
            length,
        };
    }
    const length = cursor.readUint16(syntax.littleEndian);
    return { tag, vr: onWireVr, onWireVr, length };
}

/**
 * The representation an explicit-VR value is decoded under.
 *
 * A value of 65,536 bytes or more cannot state its length in the 16 bits most
 * VRs are given, so it is written as UN with the 32-bit form instead — the
 * convention dcmjs and the wider ecosystem use, and the one every structure
 * set this application saved before the engine was written follows. The real
 * representation comes from the dictionary, without which a large contour
 * reads back as opaque bytes and the structure set fails to load.
 *
 * An undefined length is left alone: PS3.5 section 7.5 makes that UN an
 * implicit-VR sequence, which the caller handles. So is a tag the dictionary
 * does not know, whose bytes genuinely have no known meaning.
 */
function decodingVrFor(tag: Tag, onWireVr: Vr, length: number): Vr {
    if (onWireVr !== 'UN' || length === UNDEFINED_LENGTH) {
        return onWireVr;
    }
    const fromDictionary = vrForTag(tag);
    return fromDictionary === 'UN' ? onWireVr : fromDictionary;
}

/**
 * Reads elements into `dataset` until `end`, or until `stopBefore` is
 * reached.
 *
 * `end` is an absolute offset in the cursor's buffer, so the same routine
 * reads a whole dataset, a length-delimited sequence item, and the file meta
 * group.
 */
function readElementsInto(
    dataset: DicomDataset,
    cursor: ByteCursor,
    end: number,
    syntax: TransferSyntax,
    stopBefore: Tag | undefined
): void {
    while (cursor.offset < end && !cursor.atEnd) {
        if (stopBefore !== undefined) {
            const upcoming = peekTag(cursor, syntax);
            if (upcoming !== undefined && upcoming >= stopBefore) {
                return;
            }
        }
        const mark = cursor.offset;
        const delimiter = peekTag(cursor, syntax);
        if (
            delimiter === ITEM_DELIMITATION_TAG ||
            delimiter === SEQUENCE_DELIMITATION_TAG
        ) {
            // The caller that opened this undefined-length region consumes
            // its delimiter; stopping here leaves the cursor on it.
            cursor.seek(mark);
            return;
        }
        readOneElementInto(dataset, cursor, syntax);
    }
}

function peekTag(cursor: ByteCursor, syntax: TransferSyntax): Tag | undefined {
    if (cursor.remaining < 4) {
        return undefined;
    }
    const header = cursor.peekBytes(cursor.offset, 4);
    const group = syntax.littleEndian
        ? header[0] | (header[1] << 8)
        : (header[0] << 8) | header[1];
    const element = syntax.littleEndian
        ? header[2] | (header[3] << 8)
        : (header[2] << 8) | header[3];
    return makeTag(group, element);
}

function readOneElementInto(
    dataset: DicomDataset,
    cursor: ByteCursor,
    syntax: TransferSyntax
): void {
    const header = readElementHeader(cursor, syntax);

    if (isSequence(header)) {
        dataset.put(
            readSequence(cursor, header, syntax, dataset.specificCharset)
        );
        return;
    }

    if (header.length === UNDEFINED_LENGTH) {
        if (header.tag === PIXEL_DATA_TAG) {
            dataset.put(readEncapsulatedPixelData(cursor, header));
            return;
        }
        throw new DicomParseError(
            `Element ${formatTag(header.tag)} with VR ${header.vr} declares an ` +
                'undefined length, which PS3.5 permits only for sequences and for ' +
                'encapsulated pixel data.',
            cursor.offset
        );
    }

    const value = cursor.readBytes(header.length);
    dataset.put(
        DicomElement.fromSource(
            header.tag,
            header.vr,
            value,
            dataset.specificCharset,
            header.onWireVr
        )
    );
}

/**
 * Whether an element is a sequence.
 *
 * An explicit SQ says so. An undefined-length element that the dictionary
 * typed UN is also a sequence: PS3.5 section 7.5 requires a parser that meets
 * an undefined length on a UN element to read it as an implicit-VR sequence,
 * which is how private sequences survive a transcoding to explicit VR.
 */
function isSequence(header: ElementHeader): boolean {
    if (header.vr === 'SQ') {
        return true;
    }
    return header.vr === 'UN' && header.length === UNDEFINED_LENGTH;
}

function readSequence(
    cursor: ByteCursor,
    header: ElementHeader,
    syntax: TransferSyntax,
    enclosingCharset: Charset
): DicomElement {
    // A UN sequence carries implicit-VR items whatever the enclosing syntax,
    // because that is the encoding it was converted from. Decided by what the
    // stream carried rather than by how the value is being read: a
    // defined-length UN on a tag the dictionary knows is read as the sequence
    // it is, but its items are still encoded the way UN content always is.
    const itemSyntax =
        header.onWireVr === 'UN'
            ? transferSyntaxFor(IMPLICIT_VR_LITTLE_ENDIAN)
            : syntax;

    const undefinedLength = header.length === UNDEFINED_LENGTH;
    const end = undefinedLength ? cursor.length : cursor.offset + header.length;
    if (end > cursor.length) {
        throw new DicomParseError(
            `Sequence ${formatTag(header.tag)} declares ${header.length} bytes, ` +
                `which runs past the end of the ${cursor.length}-byte buffer.`,
            cursor.offset
        );
    }

    const valueStart = cursor.offset;
    const items: DicomItem[] = [];
    let holdsItems = true;

    // Each item's tag is examined before it is consumed, so that meeting
    // something which is not an item leaves the cursor no further along than
    // the item that was about to be read. A cursor only moves forward.
    while (cursor.offset < end && !cursor.atEnd) {
        const itemTag = peekTag(cursor, itemSyntax);
        if (itemTag === SEQUENCE_DELIMITATION_TAG) {
            cursor.skip(8);
            break;
        }
        if (itemTag !== ITEM_TAG || cursor.remaining < 8) {
            holdsItems = false;
            break;
        }
        cursor.skip(4);
        const itemLength = cursor.readUint32(itemSyntax.littleEndian);
        items.push(readItem(cursor, itemLength, itemSyntax, enclosingCharset));
    }

    if (!holdsItems) {
        if (undefinedLength) {
            // Nothing states where this attribute ends: its extent was to be
            // found by reading items up to the delimiter, and the items are not
            // there. Assuming a length would be assuming where the next
            // attribute begins, and every attribute after it.
            throw new DicomParseError(
                `Expected an item tag (FFFE,E000) in sequence ` +
                    `${formatTag(header.tag)}, and the sequence states no ` +
                    'length, so where it ends cannot be established.',
                cursor.offset
            );
        }
        // The attribute states its length, so its content can be kept without
        // being understood. Scanners do put a plain string in a tag the
        // dictionary types as a sequence, and under implicit VR — where the
        // representation comes from the dictionary rather than from the
        // stream — nothing in the file says otherwise.
        //
        // Permissive on the way in, unaltered on the way out: the bytes are
        // retained exactly as they arrived, so a save reproduces the attribute
        // rather than correcting or dropping it, and a study still opens
        // instead of being refused over one attribute no clinical path reads.
        const opaque = cursor.peekBytes(valueStart, header.length);
        cursor.seek(valueStart + header.length);
        return DicomElement.fromSource(
            header.tag,
            'UN',
            opaque,
            enclosingCharset,
            header.onWireVr
        );
    }

    return DicomElement.fromItems(
        header.tag,
        items,
        undefinedLength,
        header.vr,
        header.onWireVr
    );
}

function readItem(
    cursor: ByteCursor,
    itemLength: number,
    syntax: TransferSyntax,
    enclosingCharset: Charset
): DicomItem {
    const undefinedLength = itemLength === UNDEFINED_LENGTH;
    const itemEnd = undefinedLength
        ? cursor.length
        : cursor.offset + itemLength;
    if (itemEnd > cursor.length) {
        throw new DicomParseError(
            `Sequence item declares ${itemLength} bytes, which runs past the end ` +
                `of the ${cursor.length}-byte buffer.`,
            cursor.offset
        );
    }

    const itemDataset = new DicomDataset();
    // An item carries no SpecificCharacterSet of its own; PS3.3 C.12.1.1.2
    // makes the enclosing dataset's apply to every dataset nested inside it.
    // Starting an item at the default repertoire instead would decode text
    // inside a sequence — an ROI name among them — in a different character
    // set from the one the file declares. An item that does declare its own
    // set replaces this when that element is read.
    itemDataset.setCharset(enclosingCharset);
    readElementsInto(itemDataset, cursor, itemEnd, syntax, undefined);

    if (undefinedLength) {
        // Consume the item delimiter the reader stopped in front of.
        const delimiterTag = readTag(cursor, syntax);
        const delimiterLength = cursor.readUint32(syntax.littleEndian);
        if (delimiterTag !== ITEM_DELIMITATION_TAG || delimiterLength !== 0) {
            throw new DicomParseError(
                'Expected an item delimitation item (FFFE,E00D) with zero length ' +
                    `at the end of an undefined-length item, found ` +
                    `${formatTag(delimiterTag)} with length ${delimiterLength}.`,
                cursor.offset - 8
            );
        }
    } else {
        cursor.seek(itemEnd);
    }

    return { dataset: itemDataset, undefinedLength };
}

/**
 * Reads encapsulated PixelData: a Basic Offset Table followed by one or more
 * compressed fragments, terminated by a sequence delimiter (PS3.5 Annex A.4).
 *
 * Fragments stay as views over the source buffer. Nothing here decompresses
 * anything — that is the job of whichever codec the transfer syntax names,
 * and keeping the two separate is what lets an unrecognized compressed
 * syntax still parse and re-serialize intact.
 */
function readEncapsulatedPixelData(
    cursor: ByteCursor,
    header: ElementHeader
): DicomElement {
    const syntax = FILE_META_TRANSFER_SYNTAX;
    let offsetTable = new Uint8Array(0);
    const fragments: Uint8Array[] = [];
    let isFirstItem = true;

    while (!cursor.atEnd) {
        const itemTag = readTag(cursor, syntax);
        const itemLength = cursor.readUint32(syntax.littleEndian);

        if (itemTag === SEQUENCE_DELIMITATION_TAG) {
            break;
        }
        if (itemTag !== ITEM_TAG) {
            throw new DicomParseError(
                `Expected an item tag (FFFE,E000) in encapsulated pixel data, ` +
                    `found ${formatTag(itemTag)}.`,
                cursor.offset - 8
            );
        }
        if (itemLength === UNDEFINED_LENGTH) {
            throw new DicomParseError(
                'A pixel data fragment declares an undefined length; fragments ' +
                    'must state their length.',
                cursor.offset - 4
            );
        }

        const itemBytes = cursor.readBytes(itemLength);
        if (isFirstItem) {
            offsetTable = itemBytes;
            isFirstItem = false;
        } else {
            fragments.push(itemBytes);
        }
    }

    return DicomElement.fromFragments(header.tag, header.vr, {
        offsetTable,
        fragments,
    });
}

/**
 * True when a buffer carries a PS3.10 file meta group.
 *
 * False for a stored dataset, which this reader accepts and most DICOM
 * libraries do not — so this is the question to ask before handing a buffer to
 * one that does not.
 */
export function hasFileMetaGroup(buffer: ArrayBuffer): boolean {
    const bytes = new Uint8Array(buffer);
    const start = startsWithMagicAt(bytes, 0)
        ? PREAMBLE_LENGTH + DICM_MAGIC.length
        : 0;
    if (start + 4 > bytes.byteLength) {
        return false;
    }
    return (bytes[start] | (bytes[start + 1] << 8)) === 0x0002;
}

export { PREAMBLE_LENGTH, DICM_MAGIC };
